import { useRef, useEffect, useCallback, memo, useMemo } from "react";
import { ActivityIndicator, ScrollView } from "react-native";
import { LegendList, type LegendListRef } from "@legendapp/list";
import { View, Text } from "@src/components";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { Icon } from "@src/components/Icon";
import { ClipTimelineItem } from "./ClipTimelineItem";
import type { Clip } from "../types/clip";
import { PressableScale } from "pressto";
import { isWeb } from "@src/utils/web-helpers";

interface ClipsTimelineProps {
  clips: Clip[];
  selectedClipId: string | null;
  onClipSelect: (clipId: string) => void;
  timezone: string;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
}

const ITEM_WIDTH = 130;
const ITEM_HEIGHT = 76;
const WEB_SCROLLBAR_GAP = 10;

export const ClipsTimeline = memo(
  ({
    clips,
    selectedClipId,
    onClipSelect,
    timezone,
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
  }: ClipsTimelineProps) => {
    const { theme } = useUnistyles();
    const listRef = useRef<LegendListRef>(null);
    const webScrollRef = useRef<ScrollView>(null);

    const sortedClips = useMemo(() => {
      return [...clips].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    }, [clips]);

    useEffect(() => {
      if (selectedClipId && listRef.current && sortedClips.length > 0) {
        const index = sortedClips.findIndex((clip) => clip.id === selectedClipId);
        if (index !== -1) {
          // If last clip is selected, scroll to end to show Load More button
          if (index === sortedClips.length - 1 && hasNextPage) {
            listRef.current.scrollToEnd({ animated: true });
          } else {
            listRef.current.scrollToIndex({
              index,
              animated: true,
            });
          }
        }
      }
    }, [selectedClipId, sortedClips, hasNextPage]);

    const renderItem = useCallback(
      ({ item }: { item: Clip }) => (
        <ClipTimelineItem
          clip={item}
          isSelected={item.id === selectedClipId}
          onPress={() => onClipSelect(item.id)}
          width={ITEM_WIDTH}
          height={ITEM_HEIGHT}
          timezone={timezone}
        />
      ),

      [selectedClipId, onClipSelect, timezone],
    );

    const keyExtractor = useCallback((item: Clip) => item.id, []);

    // Load More button shown at the start of the list (left side)
    const renderLoadMoreButton = useCallback(() => {
      if (!hasNextPage) return null;

      return (
        <PressableScale onPress={onLoadMore} enabled={!isFetchingNextPage} style={styles.loadMoreButton}>
          <View
            w={ITEM_WIDTH}
            h={ITEM_HEIGHT + theme.spacing.xxs}
            rounded={theme.spacing.lg}
            alignItems="center"
            justifyContent="center"
            bg="surface"
            style={{ opacity: isFetchingNextPage ? 0.5 : 1 }}
          >
            {isFetchingNextPage ?
              <ActivityIndicator color={theme.colors.text as string} />
            : <>
                <Icon name="arrowRight" size={theme.spacing["5xl"]} color={theme.colors.text as string} />
                <View mt="xs">
                  <Text textStyle="xs/semibold" color="text">
                    Load More
                  </Text>
                </View>
              </>
            }
          </View>
        </PressableScale>
      );
    }, [hasNextPage, isFetchingNextPage, onLoadMore, theme.colors.text, theme.spacing]);

    if (clips.length === 0) {
      return null;
    }

    if (isWeb) {
      const handleWebWheel = (event: any) => {
        const node = (webScrollRef.current as any)?.getScrollableNode?.() ?? (webScrollRef.current as any);
        if (!node) return;
        if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
          node.scrollLeft += event.deltaY;
          event.preventDefault?.();
        }
      };

      return (
        <View style={styles.container}>
          <ScrollView
            ref={webScrollRef}
            horizontal
            showsHorizontalScrollIndicator
            contentContainerStyle={styles.webContentContainer}
            {...({ onWheel: handleWebWheel } as any)}
          >
            {sortedClips.map((item) => (
              <ClipTimelineItem
                key={item.id}
                clip={item}
                isSelected={item.id === selectedClipId}
                onPress={() => onClipSelect(item.id)}
                width={ITEM_WIDTH}
                height={ITEM_HEIGHT}
                timezone={timezone}
              />
            ))}
            {renderLoadMoreButton()}
          </ScrollView>
        </View>
      );
    }

    return (
      <View style={styles.container}>
        <LegendList
          ref={listRef}
          data={sortedClips}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          extraData={selectedClipId}
          horizontal
          showsHorizontalScrollIndicator
          contentContainerStyle={styles.contentContainer}
          ListFooterComponent={renderLoadMoreButton}
          estimatedItemSize={ITEM_WIDTH + theme.spacing.sm * 2}
          recycleItems
        />
      </View>
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  container: {
    height: ITEM_HEIGHT + theme.spacing.lg + (isWeb ? WEB_SCROLLBAR_GAP : 0),
  },
  contentContainer: {
    paddingHorizontal: theme.spacing.xs,
  },
  webContentContainer: {
    paddingHorizontal: theme.spacing.xs,
    paddingBottom: WEB_SCROLLBAR_GAP,
  },
  loadMoreButton: {
    marginRight: theme.spacing.sm,
  },
}));
